import { useStore } from '@nanostores/react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle, type ImperativePanelHandle } from 'react-resizable-panels';
import {
  CodeMirrorEditor,
  type EditorDocument,
  type EditorSettings,
  type OnChangeCallback as OnEditorChange,
  type OnSaveCallback as OnEditorSave,
  type OnScrollCallback as OnEditorScroll,
} from '~/components/editor/codemirror/CodeMirrorEditor';
import { IconButton } from '~/components/ui/IconButton';
import { PanelHeader } from '~/components/ui/PanelHeader';
import { PanelHeaderButton } from '~/components/ui/PanelHeaderButton';
import { shortcutEventEmitter } from '~/lib/hooks';
import type { FileMap } from '~/lib/stores/files';
import { themeStore } from '~/lib/stores/theme';
import { workbenchStore } from '~/lib/stores/workbench';
import { classNames } from '~/utils/classNames';
import { WORK_DIR } from '~/utils/constants';
import { renderLogger } from '~/utils/logger';
import { isMobile } from '~/utils/mobile';
import { FileBreadcrumb } from './FileBreadcrumb';
import { FileTree } from '~/components/workbench/FileTree';
import { Terminal, type TerminalRef } from '~/components/workbench/terminal/Terminal';
import { HeaderActionButtons } from '~/components/header/HeaderActionButtons.client';
import { TerminalTabs } from './terminal/TerminalTabs';

// import { DownloadModal } from '~/components/ui/DownloadModal';

interface EditorPanelProps {
  files?: FileMap;
  unsavedFiles?: Set<string>;
  editorDocument?: EditorDocument;
  selectedFile?: string | undefined;
  isStreaming?: boolean;
  onEditorChange?: OnEditorChange;
  onEditorScroll?: OnEditorScroll;
  onFileSelect?: (value?: string) => void;
  onFileSave?: OnEditorSave;
  onFileReset?: () => void;
  onDownloadClick: () => void;
}

const DEFAULT_TERMINAL_SIZE = 25;
const DEFAULT_EDITOR_SIZE = 100 - DEFAULT_TERMINAL_SIZE;

const editorSettings: EditorSettings = {
  tabSize: 2,
  fontSize: '14px',
  gutterFontSize: '12px',
};

export const EditorPanel = memo(
  ({
    files,
    unsavedFiles,
    editorDocument,
    selectedFile,
    isStreaming,
    onFileSelect,
    onEditorChange,
    onEditorScroll,
    onFileSave,
    onFileReset,
    onDownloadClick,
  }: EditorPanelProps) => {
    renderLogger.trace('EditorPanel');

    const theme = useStore(themeStore);
    const showTerminal = useStore(workbenchStore.showTerminal);

    const terminalRefs = useRef<Array<TerminalRef | null>>([]);
    const terminalToggledByShortcut = useRef(false);

    const [isModalOpen, setModalOpen] = useState(false);

    const activeFileSegments = useMemo(() => {
      if (!editorDocument) {
        return undefined;
      }

      return editorDocument.filePath.split('/');
    }, [editorDocument]);

    const activeFileUnsaved = useMemo(() => {
      return editorDocument !== undefined && unsavedFiles?.has(editorDocument.filePath);
    }, [editorDocument, unsavedFiles]);

    useEffect(() => {
      const unsubscribeFromEventEmitter = shortcutEventEmitter.on('toggleTerminal', () => {
        terminalToggledByShortcut.current = true;
      });

      const unsubscribeFromThemeStore = themeStore.subscribe(() => {
        for (const ref of Object.values(terminalRefs.current)) {
          ref?.reloadStyles();
        }
      });

      return () => {
        unsubscribeFromEventEmitter();
        unsubscribeFromThemeStore();
      };
    }, []);

    const handleDownloadClick = () => {
      setModalOpen(true);
    };

    const handleModalConfirm = () => {
      onDownloadClick();
      console.log('vwjvnkwjvn');
      setModalOpen(false);
    };

    return (
      <>
        <PanelGroup direction="vertical">
          <Panel defaultSize={showTerminal ? DEFAULT_EDITOR_SIZE : 100} minSize={20}>
            <PanelGroup direction="horizontal" className="h-full">
              {/* file explorer sidebar */}
              <Panel defaultSize={20} minSize={10} collapsible>
                {/* Monaco-style file explorer sidebar */}
                <div className="flex flex-col border-r border-alpha-elements-borderColor h-full bg-alpha-elements-background-depth-1">
                  <PanelHeader className="bg-alpha-elements-background-depth-2 font-medium text-xs uppercase tracking-wide px-4 h-9 flex items-center">
                    <div className="i-ph:tree-structure-duotone shrink-0 mr-1.5 text-alpha-elements-textSecondary" />
                    Explorer
                  </PanelHeader>
                  <FileTree
                    className="h-full monaco-file-tree px-2 py-1"
                    files={files}
                    hideRoot
                    unsavedFiles={unsavedFiles}
                    rootFolder={WORK_DIR}
                    selectedFile={selectedFile}
                    onFileSelect={onFileSelect}
                  />
                </div>
              </Panel>
              <PanelResizeHandle className="w-1 bg-alpha-elements-borderColor hover:bg-alpha-elements-accent-primary" />

              {/* code panel */}
              <Panel className="flex flex-col" defaultSize={80} minSize={20}>
                {/* Monaco-style breadcrumb header */}
                <PanelHeader className="overflow-hidden bg-alpha-elements-background-depth-1 border-b border-alpha-elements-borderColor h-9 px-2">
                  {activeFileSegments?.length && (
                    <div className="flex items-center flex-1 text-xs">
                      <FileBreadcrumb pathSegments={activeFileSegments} files={files} onFileSelect={onFileSelect} />
                      <div className="flex gap-1 ml-auto">
                        {activeFileUnsaved && (
                          <>
                            <PanelHeaderButton
                              className="text-xs px-2 py-1 hover:bg-alpha-elements-background-depth-3 rounded"
                              onClick={onFileSave}
                            >
                              <div className="i-ph:floppy-disk-duotone mr-1" />
                              Save
                            </PanelHeaderButton>
                            <PanelHeaderButton
                              className="text-xs px-2 py-1 hover:bg-alpha-elements-background-depth-3 rounded"
                              onClick={onFileReset}
                            >
                              <div className="i-ph:clock-counter-clockwise-duotone mr-1" />
                              Reset
                            </PanelHeaderButton>
                          </>
                        )}
                        {/* terminal toggle button */}
                        {/* <IconButton
                          icon="i-ph:download-duotone"
                          title="Download Code"
                          size="md"
                          onClick={handleDownloadClick}
                        /> */}
                        <div
                          className="flex items-center gap-1 cursor-pointer hover:text-alpha-elements-textPrimary"
                          onClick={() => workbenchStore.toggleTerminal(!showTerminal)}
                          title="Toggle Terminal"
                        >
                          <span className="text-xs text-alpha-elements-textSecondary">Terminal</span>
                          <div className="i-ph:terminal-window-duotone text-lg" />
                        </div>
                        <HeaderActionButtons />
                      </div>
                    </div>
                  )}
                </PanelHeader>
                <div className="h-full flex-1 overflow-hidden bg-alpha-elements-background">
                  <CodeMirrorEditor
                    theme={theme}
                    editable={!isStreaming && editorDocument !== undefined}
                    settings={editorSettings}
                    doc={editorDocument}
                    autoFocusOnDocumentChange={!isMobile()}
                    onScroll={onEditorScroll}
                    onChange={onEditorChange}
                    onSave={onFileSave}
                    className="monaco-editor-container"
                  />
                </div>
              </Panel>
              {/* <PanelResizeHandle /> */}
            </PanelGroup>
          </Panel>
          <PanelResizeHandle />
          <TerminalTabs />
        </PanelGroup>
      </>
    );
  },
);
